<script setup lang="ts">
// Aviso de pausa das operações (Medida Provisória). Reusa o StepSuspended do
// modal de depósito, pra que a pausa (home, cadastro e jogos) e o depósito
// mostrem o mesmo aviso. Textos em
// utils/deposit-suspension.ts (siteSuspensionNotices), sobrepostos pelo core.
import type { SiteSuspensionVariant } from '~/utils/deposit-suspension';

type Props = {
  variant?: SiteSuspensionVariant;
  // Aberto com o Clube Fidelidade na tela (ex.: card do hub que leva a /games
  // com os jogos pausados): o ModalRenderer fica em z-999, abaixo do hub
  // (z-9990). Com `elevated` o aviso vai direto pro body, numa camada própria
  // acima do hub e com fundo escuro próprio (o do renderer ficaria atrás do hub).
  elevated?: boolean;
};

const { variant = 'home', elevated = false } = defineProps<Props>();

type Emits = {
  close: [];
};

const emit = defineEmits<Emits>();

// Trava o scroll do fundo enquanto o aviso está montado. O composable libera
// sozinho no unmount, então não precisa destravar na mão.
useBodyScrollLock(ref(true));

const notice = computed(() => getSuspensionNotice(variant));

// Operações do site (ENG-915): religada no core enquanto o aviso está aberto, a
// parte do site volta e o aviso sai sozinho no próximo refresh do status.
const { isHomeNoticeEnabled, isGamesPaused, isRegisterPaused } =
  useFeatureFlagsNew();

const isStillPaused = computed<boolean>(() => {
  switch (variant) {
    case 'home':
      return isHomeNoticeEnabled.value;

    case 'games':
      return isGamesPaused.value;

    case 'register':
      return isRegisterPaused.value;
  }
});

// Igual ao modal de depósito: sheet colado no rodapé no mobile, card
// centralizado no desktop.
const { isMobile } = useLayout();

// Duração do slide (tem que bater com o `animation` do .card no CSS).
const SLIDE_MS = 300;

// Saída animada: o ModalRenderer desmonta o componente assim que recebe o
// `close`, então a descida acontece aqui antes de avisar.
const isClosing = ref(false);
let closeTimer: ReturnType<typeof setTimeout> | undefined;

function requestClose() {
  if (isClosing.value) return;

  isClosing.value = true;
  closeTimer = setTimeout(() => emit('close'), SLIDE_MS);
}

watch(isStillPaused, (paused) => {
  if (!paused) requestClose();
});

onBeforeUnmount(() => clearTimeout(closeTimer));
</script>

<template>
  <Teleport
    :disabled="!elevated"
    to="body"
  >
    <div
      :class="[
        $style.overlay,
        { [$style.isDesktop]: !isMobile, [$style.isElevated]: elevated },
      ]"
      @click.self="requestClose"
    >
      <div
        :class="[
          $style.card,
          { [$style.isDesktop]: !isMobile, [$style.isClosing]: isClosing },
        ]"
      >
        <button
          aria-label="Fechar"
          :class="$style.close"
          type="button"
          @click="requestClose"
        >
          <svg
            fill="none"
            height="10"
            viewBox="0 0 14 14"
            width="10"
          >
            <path
              d="M1 1L13 13M13 1L1 13"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-width="2"
            />
          </svg>
        </button>

        <CasinoGameDepositModalStepSuspended
          :notice="notice"
          @close="requestClose"
        />

        <div :class="$style.safeBottom" />
      </div>
    </div>
  </Teleport>
</template>

<style module lang="postcss">
/* Mesmo layout do modal de depósito (.gdm-overlay / .gdm-sheet). */
.overlay {
  @apply fixed inset-0 z-10 flex flex-col justify-end;

  &.isDesktop {
    @apply items-center justify-center p-4;
  }

  /* Acima de todas as telas do Clube Fidelidade (a mais alta é z-9999) e
     abaixo da game-bar (z-99999) e do modal de depósito (z-100000). */
  &.isElevated {
    z-index: 10000;
    background: rgba(0, 0, 0, 0.75);
  }
}

/* Mobile: colado ao rodapé, arredondado só no topo. Animation (não
   transition) por causa do reset global `transition: none !important`. */
.card {
  @apply relative w-full select-none;
  max-height: 92dvh;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 40px 16px 24px;
  border-radius: 20px 20px 0 0;
  background: #050c1f;
  animation: slide-up-spring 0.3s cubic-bezier(0.22, 0.61, 0.36, 1) backwards;

  &.isDesktop {
    width: 420px;
    max-width: 95vw;
    max-height: 88dvh;
    padding: 32px 24px 24px;
    border-radius: 16px;
    box-shadow:
      0 2px 8px 0 rgba(0, 0, 0, 0.06),
      0 -6px 12px 0 rgba(0, 0, 0, 0.03),
      0 14px 28px 0 rgba(0, 0, 0, 0.08),
      inset 0 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  /* `forwards` segura o último frame (fora da tela) até o desmonte; sem ele o
     card volta aberto por 1-2 frames antes de sumir. */
  &.isClosing {
    animation: slide-up-spring 0.3s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  }
}

.safeBottom {
  height: env(safe-area-inset-bottom, 0px);
}

/* Cópia local do slide-up-spring global (main.scss): no <style module> o nome
   da animação ganha hash e não acharia o keyframe global. */
@keyframes slide-up-spring {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Mesmo botão de fechar do modal de depósito (.gdm-close). */
.close {
  @apply absolute flex items-center justify-center rounded-full text-white;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
}
</style>
